import { Treemap, TreemapPoint } from 'react-vis';
import React, { useCallback, useContext, useState } from 'react';
import './TreemapChart.scss';
import {
    mapToReactVisData,
    ReactVisNode,
    sumValues,
    TreemapNode,
} from 'utils/components/TreemapChart/treemapData';
import { colours } from 'utils/components/TreemapChart/treemapColours';
import classNames from 'classnames';
import AppContext from 'app/AppContext';
import { textUtils } from '@instinet-analytics/core-lib';
import styleConstants from './variables.module.scss';
import { TooltipWithoutContainer } from '@instinet-analytics/core-lib';

const { pluralise } = textUtils;

const defaultGetColor = (d: TreemapPoint) => colours[d.colourIndex];

export interface TreeMapNodeArgs<T extends TreemapNode = TreemapNode> {
    title: string;
    value: number;
    percent: number;
    groupCount: number;
    colourIndex: number;
    data: T[];
}

export interface TreemapChartProps<T extends TreemapNode = TreemapNode> {
    width: number;
    height: number;
    data: T[];
    /*
     * Nodes with percentages less than this threshold will be grouped into one box
     */
    percentGroupingThreshold?: number;

    /*
     * Label after the count in the "Other" small percentages group label
     */
    percentageGroupingLabel?: string;
    /*
     * This will cause the hovered box to be highlighted and the cursor changed
     * Note: the "Other" small percentages node will always have drilldown enabled
     */
    canDrilldown: boolean;

    /*
     * Set to true if thid is a drilldown on the "Other" small percentages node
     */
    showOtherDrilldown: boolean;

    isSideSummary: boolean;

    onNodeClicked?: (node: TreeMapNodeArgs<T>) => void;

    getColor?: (d: TreemapPoint) => string;

    labelPrecision?: number;

    labelHidePercentSign?: boolean;

    getTooltip?: (node?: T) => string | undefined;
}

/*
 * Basic treemap component. Forces absolute values and calculates percentages.
 * Combines boxes below a percentage threshold and supports drilldown on these combines boxes
 */
export default function TreemapChart<T extends TreemapNode = TreemapNode>(
    props: TreemapChartProps<T>
) {
    const {
        width,
        height,
        canDrilldown,
        getTooltip,
        percentGroupingThreshold: propsPercentGroupingThreshold,
        percentageGroupingLabel,
        data,
        onNodeClicked,
        showOtherDrilldown,
        isSideSummary,
        labelPrecision = 0,
        labelHidePercentSign,
    } = props;

    const { numberFormat } = useContext(AppContext);
    const [tooltip, setTooltip] = useState<string | undefined>(undefined);

    const percentGroupingThreshold =
        propsPercentGroupingThreshold === undefined
            ? 5
            : propsPercentGroupingThreshold;

    const reactVisData = mapToReactVisData(
        data,
        percentGroupingThreshold,
        showOtherDrilldown,
        isSideSummary && canDrilldown
    );

    const onGetLabel = useCallback(
        (node: ReactVisNode<T>) => {
            const { title: nodeTitle, percent } = node;
            const groupCount = node.data.length;

            const formattedPercent = numberFormat.n(labelPrecision, percent);
            const title =
                groupCount <= 1
                    ? nodeTitle
                    : `${nodeTitle} ${groupCount} ${
                          percentageGroupingLabel
                              ? pluralise(groupCount, percentageGroupingLabel)
                              : ''
                      }`;

            return (
                <div
                    className={classNames(
                        'treemap-node-label',
                        !props.getColor &&
                            node.colourIndex !== undefined &&
                            'node-colour-' + node.colourIndex
                    )}
                >
                    <span className="title">{title}</span>
                    <span className="percent">
                        {formattedPercent}
                        {labelHidePercentSign ? '' : '%'}
                    </span>
                </div>
            );
        },
        [
            percentageGroupingLabel,
            numberFormat,
            labelPrecision,
            labelHidePercentSign,
            props.getColor,
        ]
    );

    const onLeafClick = useCallback(
        (node: TreemapPoint) => {
            if (onNodeClicked) {
                const visNode = node.data as ReactVisNode<T>;

                onNodeClicked(toTreeMapNodeArgs(visNode));
            }
        },
        [onNodeClicked]
    );

    const onLeafMouseover = useCallback(
        (v: TreemapPoint) => {
            if (getTooltip) {
                const tooltip = getTooltip(v.data?.data[0]);
                setTooltip(tooltip);
            }
        },
        [getTooltip, setTooltip]
    );
    const onLeafMouseOut = useCallback(() => {
        setTooltip(undefined);
    }, [setTooltip]);

    const disableDrilldownStyle = {
        borderWidth: styleConstants.leafBorderSize,
        borderColor: styleConstants.widgetBackground,
        cursor: 'auto',
        // eslint-disable-next-line @typescript-eslint/naming-convention
        '&:hover': {
            borderWidth: styleConstants.leafBorderSize,
            cursor: 'auto',
        },
    };

    for (const node of reactVisData.children || []) {
        const vn = node as ReactVisNode<T>;
        if (vn.data.length === 1 && !canDrilldown) {
            vn.style = disableDrilldownStyle;
        }
    }

    return (
        <div className="TreemapChart">
            {tooltip && (
                <TooltipWithoutContainer
                    text={tooltip}
                    tooltipVisible={Boolean(true)}
                    followCursor={true}
                />
            )}
            <Treemap
                animated={true}
                className="treemap"
                hideRootNode={true}
                width={width}
                height={height}
                margin={0}
                data={reactVisData}
                mode="squarify"
                renderMode="DOM"
                colorType="literal"
                padding={0}
                getColor={props.getColor ?? defaultGetColor}
                getSize={(d) => d.value}
                getLabel={onGetLabel}
                onLeafClick={onLeafClick}
                onLeafMouseOver={onLeafMouseover}
                onLeafMouseOut={onLeafMouseOut}
            />
        </div>
    );
}

function toTreeMapNodeArgs<T extends TreemapNode>(
    visNode: ReactVisNode<T>
): TreeMapNodeArgs<T> {
    return {
        title: visNode.title,
        percent: visNode.percent,
        colourIndex: visNode.colourIndex,
        value: sumValues(visNode.data),
        groupCount: visNode.data.length,
        data: visNode.data,
    };
}
